import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiRequest } from "@utils/ApiRequest";
import styled from "@emotion/styled";
import { Accordion, AccordionItem } from "@szhsin/react-accordion";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import SwiperContainer from "@components/swiper/SwiperContainer";
import { SwiperSlide } from "swiper/react";

const Container = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "2.4rem",
  padding: "0 2rem",
});

const GenderTab = styled.div(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: theme.fontWeight.bold,
}));

const GenderButton = styled(SwiperSlide)<{ active: boolean }>(
  ({ theme, active }) => ({
    padding: "0.8rem 2.6rem",
    backgroundColor: active ? theme.color.white : theme.color.border_gray,
    cursor: "pointer",
    whiteSpace: "nowrap",
    textAlign: "center",

    minWidth: "12rem",
  }),
);

const CategoryList = styled.div({
  display: "flex",
  flexDirection: "column",
});

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  button: { width: "100%" },

  ".two": {
    paddingLeft: "1.2rem",
  },

  ".three": {
    padding: "2.4rem 3.6rem",
  },

  ".szh-accordion__item-content": {
    transition: "height 0.25s cubic-bezier(0, 0, 0, 1)",
  },
}));

const AccordionHeader = styled.div<{ active: boolean }>(
  ({ theme, active }) => ({
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "2.4rem 2rem",
    borderBottom: `1px solid ${theme.color.border_gray}`,
    color: active ? theme.color.green : theme.color.black,
    cursor: "pointer",
    transition: "color 0.2s ease",
  }),
);

const Depth4Item = styled.div({
  fontSize: "1.6rem",
  cursor: "pointer",
});

const ItemName = styled.div({
  cursor: "pointer",
  // width: "8rem",
  textAlign: "start",
  whiteSpace: "nowrap",
});

const CategoryTab = () => {
  const theme = useTheme();
  const [depth1, setDepth1] = useState<number>(1001);
  const [depth2, setDepth2] = useState<number | null>(null);
  const [depth3, setDepth3] = useState<number | null>(null);
  const navigate = useNavigate();
  const { getCategoryList } = useApiRequest();
  const lang = localStorage.getItem("language");

  // 카테고리 검색 함수
  const handleSearch = (code: number, name: string) => {
    const categoryName = encodeURIComponent(name);
    navigate(
      `/search/category?category_id=${code}&categoryName=${categoryName}`,
    );
  };

  const { data: depth1List } = useQuery({
    queryKey: ["depth1List"],
    queryFn: () => getCategoryList(),
  });

  const { data: depth2List } = useQuery({
    queryKey: ["depth2List", depth1],
    queryFn: () => getCategoryList(depth1),
    enabled: !!depth1,
  });

  const { data: depth3List } = useQuery({
    queryKey: ["depth3List", depth2],
    queryFn: () => getCategoryList(depth2),
    enabled: !!depth2,
  });

  const { data: depth4List } = useQuery({
    queryKey: ["depth4List", depth3],
    queryFn: () => getCategoryList(depth3),
    enabled: !!depth3,
  });

  const toggleDepth2 = (e: React.MouseEvent, code: number) => {
    const item = depth2List?.find((item: any) => item.code === code);

    if (item?.has_children === 0) {
      handleSearch(
        code,
        lang === "ko" ? item?.name_ko : item?.name_en || item.name_ko,
      );
    } else {
      setDepth2((prevDepth2) => (prevDepth2 === code ? null : code));
      setDepth3(null);
    }
  };

  const toggleDepth3 = (e: React.MouseEvent, code: number) => {
    const item = depth3List?.find((item: any) => item.code === code);

    if (item?.has_children === 0) {
      handleSearch(
        code,
        lang === "ko" ? item?.name_ko : item?.name_en || item.name_ko,
      );
    } else {
      setDepth3((prevDepth3) => (prevDepth3 === code ? null : code));
    }
  };

  const handleChangeDepth1 = (e: React.MouseEvent, code: number) => {
    e.stopPropagation();
    setDepth1(code);
    setDepth2(null);
    setDepth3(null);
  };

  const handleChangeDepth2 = (e: React.MouseEvent, code: number) => {
    e.stopPropagation();
    const item = depth2List?.find((item: any) => item.code === code);
    handleSearch(
      code,
      lang === "ko" ? item?.name_ko : item?.name_en || item.name_ko,
    );
  };

  const handleChangeDepth3 = (e: React.MouseEvent, code: number) => {
    e.stopPropagation();
    const item = depth3List?.find((item: any) => item.code === code);
    handleSearch(
      code,
      lang === "ko" ? item?.name_ko : item?.name_en || item.name_ko,
    );
  };

  const handleChangeDepth4 = (e: React.MouseEvent, item: any) => {
    e.stopPropagation();
    handleSearch(
      item.code,
      lang === "ko" ? item.name_ko : item.name_en || item.name_ko,
    );
  };

  return (
    <Container>
      <GenderTab>
        {/* Depth1 Selection */}
        <SwiperContainer perView={3.5}>
          {depth1List?.map((item: any) => (
            <GenderButton
              active={depth1 === item.code}
              key={item.code}
              onClick={(e) => handleChangeDepth1(e, item.code)}>
              {lang === "ko" ? item.name_ko : item.name_en || item.name_ko}
            </GenderButton>
          ))}
        </SwiperContainer>

        <CategoryList>
          {/* Depth2 Accordion */}
          <StyledAccordion>
            {depth2List?.map((item: any) => (
              <AccordionItem
                key={item.code}
                itemKey={item.code}
                header={
                  <AccordionHeader
                    active={item.code === depth2}
                    onClick={(e) => {
                      toggleDepth2(e, item.code);
                    }}>
                    <ItemName onClick={(e) => handleChangeDepth2(e, item.code)}>
                      {lang === "ko"
                        ? item.name_ko
                        : item.name_en || item.name_ko}
                    </ItemName>

                    {/* arrow 이미지부분 */}
                    <div>
                      {item.has_children === 1 && (
                        <Image
                          src={
                            item.code === depth2
                              ? theme.image.arrow_up.default
                              : theme.image.arrow_down.default
                          }
                        />
                      )}
                    </div>
                  </AccordionHeader>
                }>
                {/* Depth3 Accordion */}
                <StyledAccordion>
                  {depth3List?.map((item: any) => (
                    <AccordionItem
                      className="two"
                      key={item.code}
                      itemKey={item.code}
                      header={
                        <AccordionHeader
                          active={item.code === depth3}
                          onClick={(e) => toggleDepth3(e, item.code)}>
                          <ItemName
                            onClick={(e: any) => {
                              handleChangeDepth3(e, item.code);
                            }}>
                            {lang === "ko"
                              ? item.name_ko
                              : item.name_en || item.name_ko}
                          </ItemName>

                          <div>
                            {item.has_children === 1 && (
                              <Image
                                src={
                                  item.code === depth3
                                    ? theme.image.arrow_up.default
                                    : theme.image.arrow_down.default
                                }
                              />
                            )}
                          </div>
                        </AccordionHeader>
                      }>
                      {/* Depth4 List */}
                      {depth4List?.map((depth4Item: any) => (
                        <Depth4Item
                          key={depth4Item.code}
                          className="three"
                          onClick={(e) => handleChangeDepth4(e, depth4Item)}>
                          {lang === "ko"
                            ? depth4Item.name_ko
                            : depth4Item.name_en || depth4Item.name_ko}
                        </Depth4Item>
                      ))}
                    </AccordionItem>
                  ))}
                </StyledAccordion>
              </AccordionItem>
            ))}
          </StyledAccordion>
        </CategoryList>
      </GenderTab>
    </Container>
  );
};

export default CategoryTab;
